/**
 * Definizioni dei provider di misura collegabili a un POD.
 * Dati puri, condivisi: il Worker li usa per validare e mascherare la
 * configurazione, la SPA per costruire dropdown e form. Erano due copie.
 */

export type MeterProviderId = 'smartgw' | 'iometer2g';

/** Passi di discovery supportati: il pannello popola il campo con un dropdown. */
export type DiscoveryStep = 'plants' | 'meters';

export interface ProviderFieldDef {
	key: string;
	label: string;
	type: 'text' | 'password';
	placeholder?: string;
	required: boolean;
	help?: string;
	/** Se valorizzato, il campo è popolabile via /connection/discover con questo step. */
	discoverable?: DiscoveryStep;
}

export interface ProviderDefinition {
	id: MeterProviderId;
	label: string;
	configFields: ProviderFieldDef[];
	/** Il provider espone una curva di carico storica scaricabile (fetchHistory). */
	supportsHistory?: boolean;
}

export const SMARTGW_DEFINITION: ProviderDefinition = {
	id: 'smartgw',
	label: 'Sinapsi Smart Gateway (SGH)',
	configFields: [
		{
			key: 'apiKey',
			label: 'eu-apikey',
			type: 'password',
			placeholder: 'Chiave generata su MySinapsi',
			required: true,
			help: 'Generata nella sezione dedicata di MySinapsi (richiede pacchetto licenze attivo).',
		},
		{
			key: 'plantId',
			label: 'Impianto',
			type: 'text',
			placeholder: 'es. 205',
			required: true,
			help: 'Selezionabile dopo aver inserito la eu-apikey (endpoint /plant).',
			discoverable: 'plants',
		},
		{
			key: 'meterId',
			label: 'Contatore',
			type: 'text',
			placeholder: 'es. 70453441',
			required: true,
			help: 'Selezionabile dopo aver scelto l’impianto (endpoint /meters/{plant_id}).',
			discoverable: 'meters',
		},
	],
};

/**
 * Sinapsi IoMeter2G (docs/API SIN.IOMETER2G Rev 2.9.1).
 * Cloud SGH: header `x-apikey`; risorse indirizzate da {activation_key} (UUID
 * del device) + {pod} (codice POD del misuratore 2G). Adatto come lettore M1
 * (energia attiva prelevata).
 */
export const IOMETER2G_DEFINITION: ProviderDefinition = {
	id: 'iometer2g',
	label: 'Sinapsi IoMeter2G',
	supportsHistory: true,
	configFields: [
		{
			key: 'apiKey',
			label: 'x-apikey',
			type: 'password',
			placeholder: 'Authorization Token SGH',
			required: true,
			help: 'Token x-apikey fornito da Sinapsi per l’ambiente di produzione (app.sghiot.com).',
		},
		{
			key: 'activationKey',
			label: 'Activation Key',
			type: 'text',
			placeholder: 'es. sozmf1-ocapf-7dm8x-ay7zs-a1f1w',
			required: true,
			help: 'UUID del device IoMeter2G (riportato sull’etichetta / QR code).',
		},
		{
			key: 'pod',
			label: 'POD',
			type: 'text',
			placeholder: 'es. IT006E00768000',
			required: true,
			help: 'Codice POD del misuratore intelligente 2G associato al device.',
		},
	],
};

export const PROVIDER_DEFINITIONS: ProviderDefinition[] = [
	SMARTGW_DEFINITION,
	IOMETER2G_DEFINITION,
];

/**
 * Provider mostrati nel dropdown come "in arrivo" (non selezionabili).
 * Servono a comunicare che l'integrazione è estensibile.
 */
export const COMING_SOON_PROVIDERS: { id: string; label: string }[] = [
	{ id: 'e-distribuzione', label: 'E-Distribuzione (Chain 2)' },
	{ id: 'shelly', label: 'Shelly Pro 3EM' },
];

/** Valore segnaposto restituito al client al posto dei secret salvati. */
export const SECRET_MASK = '••••••••';
